import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { Search, ShoppingCart, Plus, Minus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { resolveImage } from "@/lib/image";

const DEFAULT_SETTINGS = {
  tax_enabled: true,
  tax_rate: 0.0975,
  tax_mode: "exclusive",
  currency: "USD",
};

export default function POS() {
  const [tab, setTab] = useState("restaurant");
  const [menu, setMenu] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [selectedSizes, setSelectedSizes] = useState({});
  const [comboSelections, setComboSelections] = useState({});
  const [riceSelections, setRiceSelections] = useState({});
  const [q, setQ] = useState("");
  const { items, addItem, updateQty, removeItem, subtotal, clear } = useCart();
  const [form, setForm] = useState({ customer_name: "Walk-in", customer_phone: "555-0000", payment_method: "manual_card", notes: "" });

  useEffect(() => {
    api.get("/menu").then(({ data }) => setMenu(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/settings/pos").then(({ data }) => setSettings({ ...DEFAULT_SETTINGS, ...data })).catch(() => {});
  }, []);

  const list = tab === "restaurant" ? menu : products;
  const filtered = list.filter((i) => q === "" || i.name.toLowerCase().includes(q.toLowerCase()));

  const saleType = (it) => it.sale_type || (it.combo_eligible ? "combo" : it.party_tray_eligible ? "party_tray" : "ala_carte");

  const getSelectedSize = (it) => {
    if (saleType(it) === "combo" || !it.sizes?.length) return null;
    const selected = selectedSizes[it.id] || it.sizes[0].label;
    return it.sizes.find((s) => s.label === selected) || it.sizes[0];
  };

  const getPrice = (it) => {
    const selectedSize = getSelectedSize(it);
    return Number(selectedSize?.price ?? it.price ?? 0);
  };

  const toggleComboEntree = (itemId, entree, max) => {
    const current = comboSelections[itemId] || [];
    const next = current.includes(entree)
      ? current.filter((x) => x !== entree)
      : current.length < max
        ? [...current, entree]
        : current;
    setComboSelections({ ...comboSelections, [itemId]: next });
  };

  const add = (it) => {
    const selectedSize = getSelectedSize(it);
    const price = getPrice(it);
    const type = saleType(it);
    const entrees = comboSelections[it.id] || [];
    const rice = riceSelections[it.id] || it.rice_options?.[0] || "";

    if (type === "combo" && entrees.length !== Number(it.combo_entree_count || 0)) {
      toast.error(`Select exactly ${it.combo_entree_count} entree(s) for this combo.`);
      return;
    }

    addItem({
      item_type: tab === "restaurant" ? (type === "party_tray" ? "catering" : "restaurant") : "grocery",
      item_id: it.id,
      name: selectedSize ? `${it.name} (${selectedSize.label})` : it.name,
      unit_price: price,
      image: it.image,
      options: type === "combo"
        ? { combo_entrees: entrees, rice }
        : selectedSize
          ? { size: selectedSize.label }
          : {},
    });
  };

  const taxableSubtotal = items
    .filter((i) => i.item_type !== "grocery")
    .reduce((s, i) => s + i.unit_price * i.quantity, 0);

  let tax = 0;
  let total = subtotal;

  if (settings.tax_enabled) {
    if (settings.tax_mode === "inclusive") {
      tax = settings.tax_rate > 0 ? taxableSubtotal - taxableSubtotal / (1 + Number(settings.tax_rate || 0)) : 0;
      total = subtotal;
    } else {
      tax = taxableSubtotal * Number(settings.tax_rate || 0);
      total = subtotal + tax;
    }
  }

  const placeOrder = async () => {
    if (!items.length) return toast.error("Cart empty");
    try {
      const { data } = await api.post("/orders", { ...form, items, fulfillment: "pickup" });
      toast.success(`Order ${data.order_number} placed`);
      clear();
    } catch {
      toast.error("Failed to place order");
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-5" data-testid="pos-page">
      <div>
        <div className="flex items-center gap-3 mb-4">
          <h1 className="font-heading text-3xl text-[#FAF9F6]">POS Terminal</h1>
        </div>

        <div className="flex gap-2 mb-4">
          {["restaurant", "grocery"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`pos-tab-${t}`} className={`px-5 py-2 text-xs font-accent uppercase tracking-wider ${tab === t ? "bg-[#D4AF37] text-[#050505]" : "border border-[#D4AF37]/40 text-[#D4AF37]"}`}>
              {t}
            </button>
          ))}

          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-[#D4AF37] absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." data-testid="pos-search" className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 text-[#FAF9F6] pl-9 pr-3 py-2 rounded" />
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[calc(100vh-220px)] overflow-y-auto">
          {filtered.map((it) => {
            const selectedSize = getSelectedSize(it);
            const price = getPrice(it);

            return (
              <button key={it.id} onClick={() => add(it)} data-testid={`pos-item-${it.id}`} className="admin-card p-3 text-left hover:border-[#D4AF37]/50">
                {it.image && <div className="h-20 bg-cover bg-center rounded mb-2" style={{ backgroundImage: `url(${resolveImage(it.image)})` }} />}
                <div className="font-heading text-sm text-[#FAF9F6] line-clamp-2">{it.name}</div>

                {saleType(it) !== "combo" && it.sizes?.length > 0 && (
                  <select
                    value={selectedSize?.label || ""}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setSelectedSizes({ ...selectedSizes, [it.id]: e.target.value })}
                    className="w-full mt-2 bg-[#0B0E14] border border-[#D4AF37]/30 text-[#FAF9F6] text-xs px-2 py-1 rounded"
                  >
                    {it.sizes.map((s) => (
                      <option key={s.label} value={s.label}>{s.label}</option>
                    ))}
                  </select>
                )}

                {saleType(it) === "combo" && (
                  <div className="mt-2 space-y-2" onClick={(e) => e.stopPropagation()}>
                    {it.rice_options?.length > 0 && (
                      <select
                        value={riceSelections[it.id] || it.rice_options[0]}
                        onChange={(e) => setRiceSelections({ ...riceSelections, [it.id]: e.target.value })}
                        className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 text-[#FAF9F6] text-xs px-2 py-1 rounded"
                      >
                        {it.rice_options.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    )}
                    <div className="text-[10px] text-[#FAF9F6]/50">
                      Entrees {(comboSelections[it.id] || []).length}/{it.combo_entree_count}
                    </div>
                    <div className="max-h-28 overflow-y-auto space-y-1">
                      {(it.combo_choices || []).map((choice) => (
                        <label key={choice} className="flex items-center gap-1 text-[10px] text-[#FAF9F6]/80">
                          <input
                            type="checkbox"
                            checked={(comboSelections[it.id] || []).includes(choice)}
                            onChange={() => toggleComboEntree(it.id, choice, Number(it.combo_entree_count || 0))}
                          />
                          {choice}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="text-xs text-[#D4AF37] mt-1">${price.toFixed(2)}</div>
              </button>
            );
          })}
        </div>
      </div>

      <aside className="admin-card p-5 sticky top-0 h-fit lg:max-h-[calc(100vh-100px)] overflow-y-auto" data-testid="pos-cart">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-heading text-xl text-[#D4AF37]"><ShoppingCart className="w-5 h-5 inline mr-2" />Order</h3>
          {items.length > 0 && <button onClick={clear} className="text-xs text-[#E8A87C]"><X className="w-3 h-3 inline" /> Clear</button>}
        </div>

        <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="pos-cust" placeholder="Customer" className="w-full mb-2 bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-sm rounded text-[#FAF9F6]" />
        <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="pos-phone" placeholder="Phone" className="w-full mb-3 bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-sm rounded text-[#FAF9F6]" />

        <div className="space-y-2 max-h-72 overflow-y-auto mb-3">
          {items.map((it, idx) => (
            <div key={idx} className="bg-[#0B0E14] p-2 rounded text-sm flex items-center gap-2">
              <div className="flex-1">
                <div className="text-[#FAF9F6]">{it.name}</div>
                {it.options?.rice && <div className="text-[10px] text-[#FAF9F6]/45">Rice: {it.options.rice}</div>}
                {it.options?.combo_entrees?.length > 0 && <div className="text-[10px] text-[#FAF9F6]/45">{it.options.combo_entrees.join(", ")}</div>}
                {it.options?.size && <div className="text-[10px] text-[#FAF9F6]/45">Size: {it.options.size}</div>}
                <div className="text-xs text-[#FAF9F6]/50">${it.unit_price.toFixed(2)}</div>
              </div>
              <button onClick={() => updateQty(idx, it.quantity - 1)} className="w-6 h-6 bg-[#222] rounded text-[#D4AF37]"><Minus className="w-3 h-3 mx-auto" /></button>
              <span className="w-6 text-center">{it.quantity}</span>
              <button onClick={() => updateQty(idx, it.quantity + 1)} className="w-6 h-6 bg-[#222] rounded text-[#D4AF37]"><Plus className="w-3 h-3 mx-auto" /></button>
              <button onClick={() => removeItem(idx)} className="text-[#E8A87C]"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {items.length === 0 && <div className="text-center text-xs text-[#FAF9F6]/30 py-8">Tap items to add</div>}
        </div>

        <div className="border-t border-[#D4AF37]/20 pt-3 space-y-1 text-sm">
          <div className="flex justify-between text-[#FAF9F6]/70"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
          <div className="flex justify-between text-[#FAF9F6]/70"><span>Tax {settings.tax_enabled ? `(${(Number(settings.tax_rate || 0) * 100).toFixed(2)}%)` : "(Off)"}</span><span>${tax.toFixed(2)}</span></div>
          <div className="flex justify-between font-heading text-xl text-[#D4AF37] pt-1"><span>Total</span><span data-testid="pos-total">${total.toFixed(2)}</span></div>
        </div>

        <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} data-testid="pos-payment" className="w-full mt-3 bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-sm rounded text-[#FAF9F6]">
          <option value="manual_card">Card (terminal)</option>
          <option value="cash_on_pickup">Cash</option>
          <option value="pay_at_store">Pay at Store</option>
        </select>

        <button onClick={placeOrder} data-testid="pos-place" className="btn-chow w-full mt-3">Place Order</button>
      </aside>
    </div>
  );
}
